import { Check, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { cn } from 'lib/utils';
import { PlanSeats, seatPriceLabel } from 'features/subscription/utils/seat-pools';
import { availableSeatsLabel } from './plan-actions';

interface SeatPlanCellProps {
  current?: PlanSeats;
  planSeats: PlanSeats[];
  disabled?: boolean;
  /** Replaces the pill's text, as the bulk menu does. */
  label?: string;
  /** Restyles the trigger, as the Teams People table does. */
  triggerClassName?: string;
  /** The plan picked. Everyone keeps a plan: there is no "no plan", only switching. */
  onPick: (entry: PlanSeats) => void;
}

/** The Plan column: who is on which plan, and the menu that moves them. */
export const SeatPlanCell = ({ current, planSeats, disabled, label, triggerClassName, onPick }: SeatPlanCellProps) => {
  const { t } = useTranslation();

  const hint = (entry: PlanSeats) => {
    if (entry.code === current?.code) return t('CURRENT_PLAN', 'Current plan');
    if (entry.awaitingPayment) return t('PLAN_WAITING_FOR_PAYMENT', 'Waiting for its first payment');
    if (entry.freeThisPeriod > 0) return availableSeatsLabel(t, entry.freeThisPeriod);
    if (!entry.canAddSeats) return t('NO_SEATS_AVAILABLE_FIXED', 'No seats available. This plan has a fixed number.');
    const price = seatPriceLabel(t, entry);
    if (!price) return t('NO_SEATS_AVAILABLE', 'No seats available');
    return entry.purchased > 0
      ? t('NO_SEATS_AVAILABLE_ADD_ONE', { price, defaultValue: 'No seats available · add one for {{price}}' })
      : t('BUY_FIRST_SEAT', { price, defaultValue: 'Buy a seat for {{price}}' });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        asChild
        disabled={disabled || planSeats.length === 0}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium whitespace-nowrap transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60',
            !current && 'border-dashed font-normal text-muted-foreground',
            triggerClassName
          )}
        >
          {label ?? (current ? current.name : t('GIVE_A_PLAN', 'Give a plan'))}
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuLabel className="text-xs font-normal uppercase tracking-wide text-muted-foreground">
          {t('PLAN', 'Plan')}
        </DropdownMenuLabel>
        {planSeats.map((entry) => {
          const selected = entry.code === current?.code;
          return (
            <DropdownMenuItem
              key={entry.code}
              disabled={!selected && (entry.awaitingPayment || (entry.freeThisPeriod === 0 && !entry.canAddSeats))}
              onSelect={() => !selected && onPick(entry)}
              className="h-auto items-start gap-2 py-2"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium">{entry.name}</p>
                <p className={cn('text-xs text-muted-foreground', !selected && entry.freeThisPeriod > 0 && 'text-success')}>
                  {hint(entry)}
                </p>
              </div>
              {selected && <Check className="mt-0.5 h-4 w-4 shrink-0" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
